import { Panel } from 'components/VisualStudioPlayground/Panel/Panel'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { track } from '@tldraw/tldraw'
import { ColorsPanelProps } from './Types'
import { useEffect, useMemo, useState } from 'react'
import { isEqual } from 'lodash'
import { Color, SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { TRANSPARENT_COLOR } from './constants'
import { hasTransparency, isFullyTransparent } from 'components/VisualStudioPlayground/VisualEditor/helpers'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { SolidColorsPanelBody } from './SolidColorsPanelBody'
import { GradientColorsPanelBody } from './GradientColorsPanelBody'
import { getDefaultColor } from './utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Item } from '@react-stately/collections'

const ColorsPanel = track(
  observer(({ allowGradient, allowTransparency, defaultColor, onChange, ...props }: ColorsPanelProps) => {
    const { designStore, drawersStore } = useRootStore()
    const { isMobile } = useResponsiveDesign()

    const [newColor, setNewColor] = useState<Color>()
    const [selectedColor, setSelectedColor] = useState(defaultColor)

    useEffect(() => {
      setSelectedColor(defaultColor)
      setNewColor(undefined)
    }, [defaultColor])

    const documentColors = useMemo(() => {
      let colors: Array<Color> = []
      if (newColor && !isEqual(newColor, defaultColor)) {
        colors.push(newColor)
      }
      if (defaultColor) {
        colors.push(defaultColor)
      }
      colors.push(...props.colors.filter((c) => !isEqual(c, defaultColor) && !isFullyTransparent(c)))
      if (!allowTransparency) {
        colors = colors.filter((c) => !hasTransparency(c))
      }
      if (!allowGradient) {
        colors = colors.filter((c) => c.type !== 'linearGradient')
      }
      if (allowTransparency) {
        colors.push(TRANSPARENT_COLOR)
      }
      return colors
    }, [newColor, defaultColor, props.colors, allowTransparency, allowGradient])

    const handleSelect = (color: Color) => {
      setNewColor(undefined)
      setSelectedColor(color)
      onChange(color)
      if (isMobile) drawersStore.resetSnap()
    }

    const handleNewColorChange = (color: Color) => {
      setNewColor(color)
      setSelectedColor(color)
      onChange(color)
      if (isMobile) drawersStore.resetSnap()
    }

    return (
      <Panel title="Colors" onClose={() => designStore?.closePanel()} filled>
        {allowGradient && (
          <SegmentedControl
            isFullWidth
            activeItem={selectedColor.type}
            onItemClick={(newValue) => {
              if (selectedColor.type === newValue) {
                return
              }

              handleSelect(getDefaultColor(selectedColor))
            }}
          >
            <Item key="solidColor">Solid Color</Item>
            <Item key="linearGradient">Gradient</Item>
          </SegmentedControl>
        )}

        {selectedColor.type === 'solidColor' && (
          <SolidColorsPanelBody
            allowTransparency={allowTransparency}
            selectedColor={selectedColor}
            documentSolidColors={documentColors.filter((c) => c.type === 'solidColor') as SolidColor[]}
            onSelect={handleSelect}
            onNewColorChange={handleNewColorChange}
          />
        )}
        {selectedColor.type === 'linearGradient' && allowGradient && (
          <GradientColorsPanelBody
            documentColors={documentColors}
            selectedColor={selectedColor}
            onSelect={handleSelect}
            onNewColorChange={handleNewColorChange}
          />
        )}
      </Panel>
    )
  }),
)

export { ColorsPanel }
